// Pause rules for a pack or training plan (services.pause_rules) — the same
// five settings a plan has in its Features tab.
export interface PauseRulesForm {
  enabled: boolean;
  maxTotalDays: string;
  requireReason: boolean;
  extendsValidity: boolean;
  memberCanPause: boolean;
}

export function pauseRulesDefaults(raw: any): PauseRulesForm {
  return {
    enabled: raw?.enabled === true,
    maxTotalDays: raw?.maxTotalDays ? String(raw.maxTotalDays) : '',
    requireReason: raw?.requireReason === true,
    extendsValidity: raw?.extendsValidity !== false,
    memberCanPause: raw?.memberCanPause === true,
  };
}

/** The body value: null turns pausing off. */
export function pauseRulesBody(f: PauseRulesForm) {
  if (!f.enabled) return null;
  return {
    enabled: true,
    maxTotalDays: f.maxTotalDays ? parseInt(f.maxTotalDays) : null,
    requireReason: f.requireReason,
    extendsValidity: f.extendsValidity,
    memberCanPause: f.memberCanPause,
  };
}

function Check({ checked, onChange, title, hint }: { checked: boolean; onChange: (v: boolean) => void; title: string; hint: string }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer">
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} className="w-4 h-4 mt-0.5 accent-violet-600" />
      <span>
        <span className="block text-sm font-bold text-gray-700">{title}</span>
        <span className="block text-[11px] text-gray-400">{hint}</span>
      </span>
    </label>
  );
}

export default function PauseRulesFields({ value, onChange }: { value: PauseRulesForm; onChange: (v: PauseRulesForm) => void }) {
  const set = <K extends keyof PauseRulesForm>(k: K, v: PauseRulesForm[K]) => onChange({ ...value, [k]: v });
  return (
    <div className="col-span-2 rounded-2xl border border-gray-100 bg-gray-50/60 p-4 space-y-3">
      <Check
        checked={value.enabled}
        onChange={v => set('enabled', v)}
        title="Can be paused"
        hint="On its own, from the member's profile — and weekly slots follow the pause."
      />
      {value.enabled && (
        <div className="space-y-3 pl-7">
          <div className="max-w-[220px]">
            <label className="label-xs">Most pause days in total</label>
            <input type="number" min={1} value={value.maxTotalDays} onChange={e => set('maxTotalDays', e.target.value)} placeholder="No limit" className="field-input" />
          </div>
          <Check
            checked={value.extendsValidity}
            onChange={v => set('extendsValidity', v)}
            title="A pause moves the end date"
            hint="Also applies when the member's plan is paused. Off: the end date stays."
          />
          <Check checked={value.requireReason} onChange={v => set('requireReason', v)} title="Require a reason" hint="Asked of members and staff alike." />
          <Check
            checked={value.memberCanPause}
            onChange={v => set('memberCanPause', v)}
            title="Members can pause it themselves"
            hint="In the Gympe app and member web, within these rules. Approved at once."
          />
        </div>
      )}
    </div>
  );
}
